import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';

@Component({
  selector: 'app-link-expired',
  templateUrl: './link-expired.component.html',
  styleUrls: ['./link-expired.component.scss'],
})
export class LinkExpiredComponent implements OnInit, OnDestroy {
  email: string = '';
  phone: string | null = null;
  isLoading = false;
  errorMessage: string = '';

  lastSendTime: number | null = null;
  sendCodePermission = false;

  form: FormGroup<any> = new FormGroup({});
  subscriptions: Subscription[] = [];

  private cooldownInterval: any;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.email = this.route.snapshot.queryParamMap.get('email') || '';
    this.phone = (history.state?.phone as string | undefined) ?? null;

    this.form = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    });

    // Um novo link de 10 min já foi disparado pelo OneTimeLinkComponent ao detectar a expiração.
    this.lastSendTime = new Date().getTime();

    this.cooldownInterval = setInterval(() => {
      const now = new Date().getTime();
      this.sendCodePermission = !this.lastSendTime || now - this.lastSendTime > 1000 * 60;
    }, 1000);

    this.subscriptions.push(
      this.form.get('code')?.valueChanges.subscribe(() => {
        if (this.errorMessage) this.errorMessage = '';
      }) || new Subscription()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    clearInterval(this.cooldownInterval);
  }

  getDiffLastTimeAndNowInSec(): number {
    if (!this.lastSendTime) return 0;
    return 60 - Math.floor((new Date().getTime() - this.lastSendTime) / 1000);
  }

  async resendCode(): Promise<void> {
    if (!this.email) return;
    try {
      this.isLoading = true;
      this.phone = await this.userService.resendExpiredLink(this.email);
      this.lastSendTime = new Date().getTime();
      this.sendCodePermission = false;
    } catch (error) {
      // Nothing was actually resent — leave the cooldown alone so the user can retry immediately.
      console.error('[LinkExpired] Failed to resend code:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async submit(): Promise<void> {
    if (!this.email) return;
    const code = this.form.get('code')?.value;
    if (!code) return;

    try {
      this.isLoading = true;
      this.errorMessage = '';

      // O código de 6 dígitos pode ter chegado por e-mail ou por SMS (quando
      // há telefone cadastrado, ambos são reenviados em paralelo). Tenta e-mail
      // primeiro e só cai para SMS se houver telefone e a verificação por
      // e-mail falhar.
      const emailResult = await this.userService.logInWithOTP(this.email, code);
      let error = emailResult.error;

      if (error && this.phone) {
        const smsResult = await this.userService.verifySmsOtp(this.phone, code);
        error = smsResult.error;
      }

      if (error) throw error;

      await this.userService.waitForCurrentUser();
      const redirectPath = localStorage.getItem('postLoginRedirect');
      if (redirectPath) {
        localStorage.removeItem('postLoginRedirect');
        this.router.navigateByUrl(redirectPath);
      } else {
        this.router.navigateByUrl('/client');
      }
    } catch (error: any) {
      console.error('[LinkExpired] Failed to verify code:', error);
      this.errorMessage = 'InvalidOrExpiredCode';
    } finally {
      this.isLoading = false;
    }
  }
}
